import type { FC, ReactNode } from 'react'
import type { Settings } from '@/types/content'

import { useCallback, useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { Menu } from 'lucide-react'
import { cn } from '@/lib/utils'
import { BrandMark } from '@/components/generals/brand-mark'
import { CtaButton } from '@/components/generals/cta-button'
import { Container } from '@/components/generals/container'
import { MobileMenu } from '@/components/generals/mobile-menu'

import { mainNavigation } from '@/constants/site'

interface SiteHeaderProps {
    settings: Settings
    overlay?: boolean
}

export const isActivePath = (pathname: string, href: string): boolean =>
    href === '/' ? pathname === '/' : pathname === href || pathname.startsWith(`${href}/`)

// Fixed site header. Over a dark hero it starts transparent and turns solid once the page scrolls.
export const SiteHeader: FC<SiteHeaderProps> = ({ settings, overlay = false }): ReactNode => {
    const router = useRouter()
    const [scrolled, setScrolled] = useState(false)
    const [menuOpen, setMenuOpen] = useState(false)
    const menuButtonRef = useRef<HTMLButtonElement>(null)

    useEffect(() => {
        const update = () => setScrolled(window.scrollY > 24)

        update()
        window.addEventListener('scroll', update, { passive: true })

        return () => window.removeEventListener('scroll', update)
    }, [])

    const transparent = overlay && !scrolled
    const tone = transparent ? 'dark' : 'light'

    const closeMenu = useCallback(() => {
        setMenuOpen(false)
        menuButtonRef.current?.focus()
    }, [])

    return (
        <>
            <header
                className={cn(
                    'fixed inset-x-0 top-0 z-40 transition-[background-color,border-color,color,backdrop-filter] duration-500 ease-brand',
                    transparent
                        ? 'border-b border-transparent bg-transparent text-bone'
                        : 'border-b border-ink/10 bg-bone/90 text-ink backdrop-blur-md supports-[backdrop-filter]:bg-bone/80'
                )}
            >
                {transparent && (
                    <div aria-hidden='true' className='pointer-events-none absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-ink/55 to-transparent' />
                )}

                <Container className='relative flex h-[var(--header-height)] items-center justify-between gap-6'>
                    <BrandMark settings={settings} tone={tone} />

                    <nav aria-label='Hauptnavigation' className='hidden xl:block'>
                        <ul className='flex items-center gap-1 2xl:gap-2'>
                            {mainNavigation.map(item => {
                                const active = isActivePath(router.pathname, item.href)

                                return (
                                    <li key={item.href}>
                                        <Link
                                            href={item.href}
                                            aria-current={active ? 'page' : undefined}
                                            className={cn(
                                                'relative flex h-10 items-center rounded-full px-3 text-[0.9375rem] font-medium transition-colors duration-300 2xl:px-4',
                                                transparent ? 'hover:bg-bone/10' : 'hover:bg-ink/5',
                                                active ? '' : transparent ? 'text-bone/80 hover:text-bone' : 'text-ink/70 hover:text-ink'
                                            )}
                                        >
                                            {item.label}

                                            {active && (
                                                <span aria-hidden='true' className='absolute bottom-1 left-1/2 size-1 -translate-x-1/2 rounded-full bg-ember' />
                                            )}
                                        </Link>
                                    </li>
                                )
                            })}
                        </ul>
                    </nav>

                    <div className='flex items-center gap-3'>
                        <CtaButton settings={settings} tone={tone} className='hidden sm:inline-flex' />

                        <button
                            ref={menuButtonRef}
                            type='button'
                            onClick={() => setMenuOpen(true)}
                            aria-expanded={menuOpen}
                            aria-controls='mobile-menu'
                            aria-label='Menü öffnen'
                            className={cn(
                                'flex size-12 items-center justify-center rounded-full border transition-colors duration-300 xl:hidden',
                                transparent ? 'border-bone/30 hover:bg-bone/10' : 'border-ink/15 hover:bg-ink/5'
                            )}
                        >
                            <Menu className='size-5' strokeWidth={1.75} />
                        </button>
                    </div>
                </Container>
            </header>

            <MobileMenu open={menuOpen} onClose={closeMenu} settings={settings} />
        </>
    )
}
